import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/common/PageHeader';
import SectionHeading from '../components/ui/SectionHeading';
import ServiceSubscribeCard from '../components/services/ServiceSubscribeCard';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import GlossaryAutoLinker from '../components/glossary/GlossaryAutoLinker';

const Services: React.FC = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI Workflow Automation & Adoption Services",
    "description": "Three-stage AI adoption for New Zealand SMEs: Business Discovery & AI Roadmap, Implementation, and ongoing Support.",
    "provider": {
      "@type": "Organization",
      "name": "ThinkTomorrow.AI",
      "url": "https://thinktomorrow.ai/"
    },
    "areaServed": { "@type": "Country", "name": "New Zealand" },
    "serviceType": "AI Consulting & Workflow Automation"
  };
  return (
    <>
      <Helmet>
        <title>AI & Workflow Automation Services NZ | ThinkTomorrow.AI</title>
        <meta name="description" content="Three-stage AI adoption for NZ SMEs: Business Discovery & AI Roadmap, Implementation, and ongoing Support. Discovery from $900." />
        <link rel="canonical" href="https://thinktomorrow.ai/services" />
        <meta property="og:title" content="AI & Workflow Automation Services NZ | ThinkTomorrow.AI" />
        <meta property="og:description" content="Discovery, roadmap, implementation, and support for NZ small and medium businesses." />
        <meta property="og:url" content="https://thinktomorrow.ai/services" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-24">
        <PageHeader 
          title="Optimising Your Business for Future Growth"
          subtitle="Comprehensive discovery, Plan, Implementation and support."
        />
        
        {/* Digital Services Packages */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading 
              title="The Three Stage Process" 
              subtitle="Thorough, detailed and usable."
              centered
            />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
              {/* Business Discovery & AI Roadmap */}
              <ServiceSubscribeCard 
                productId="business-discovery"
                title="Business Discovery & AI Roadmap"
                description={<GlossaryAutoLinker>Gain the clarity you need before committing to any build. We run a structured discovery process including workflow and process mapping, identify high-impact opportunities, and produce a practical AI roadmap aligned with NZ privacy and responsible AI guidance. You'll know exactly what to do next—and why it makes sense.</GlossaryAutoLinker>}
                customPrice="From $900"
                features={[
                  "Structured discovery workshop with business leaders",
                  "End-to-end workflow and process mapping",
                  "Identification of high-impact, low-risk AI and automation opportunities",
                  "Tailored AI adoption roadmap and prioritised next steps",
                  "ROI model showing projected time, cost, and efficiency gains",
                  "Governance, data, and risk considerations aligned with NZ privacy and responsible AI guidance",
                  "Clear, actionable recommendations"
                ]}
                note="Final pricing depends on scope and complexity. Most discovery projects fall within $900–$2,500."
                ctaText="Start Your Discovery"
                isSubscription={false}
              />
              
              {/* Guided Implementation & Automation Delivery */}
              <ServiceSubscribeCard 
                productId="infrastructure-build"
                title="Guided Implementation & Automation Delivery"
                description={<GlossaryAutoLinker>Scoped, guided delivery based on your approved roadmap. We build the workflow automation infrastructure that removes manual work and reduces errors—with human oversight, testing, and client sign-off at each stage. Nothing goes live without your approval.</GlossaryAutoLinker>}
                customPrice="From $6,500"
                features={[
                  "Systems design and architecture planning aligned to the roadmap",
                  "Workflow automation and AI agent build (e.g., CRM automation, AI call agents, chatbots)",
                  "CRM and software integrations (e.g., GoHighLevel, Xero, industry tools)",
                  "Data flows, routing logic, access controls, and governance setup",
                  "Phased implementation, testing, and refinement",
                  "Staff onboarding and handover documentation"
                ]}
                note={<GlossaryAutoLinker>All builds are scoped per the approved roadmap with human-in-the-loop oversight. High-risk or regulated processes excluded unless explicitly agreed. Timelines vary by complexity, integrations, and data readiness. Most SME builds fall within $6,500–$25,000+.</GlossaryAutoLinker>}
                ctaText="Discuss Your Build"
                isSubscription={false}
              />
              
              {/* Oversight, Maintenance & Optimisation */}
              <ServiceSubscribeCard 
                productId="ongoing-support"
                title="Oversight, Maintenance & Optimisation"
                description="Protect your investment and ensure long-term value. We continuously monitor your AI systems, update workflows as your business evolves, maintain governance oversight, and provide monthly reporting so you always know the value your automation is generating."
                customPrice="From $120 / month"
                features={[
                  "Ongoing performance monitoring and optimisation",
                  "Breakage, error, and edge-case checks",
                  "Workflow updates as tools, processes, or requirements change",
                  "Compliance, accuracy, and governance oversight",
                  "Monthly reporting against baseline metrics (time saved, efficiency, outcomes)",
                  "Optional priority support"
                ]}
                note="Pricing depends on volume, complexity, and workload. Most plans fall within $120–$950 per month."
                ctaText="Book Ongoing Support"
                isSubscription={false}
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Services;
